import { Controller } from '@hotwired/stimulus';

const titlePartsSeparator = ' | ';

// Ordered by how well the heading describes the page. The accessibility helper
// headings the layout renders are skipped, they are not what the user reads.
const headingSelectors = [
  '#content-body .PageHeader-title',
  '#content-body .PageHeader-titleBar h1, #content-body .PageHeader-titleBar h2',
  '#content-body h1',
];

const hiddenHeadingClasses = ['hidden-for-sighted', 'accessibility-helper'];

// Mirrors the page title into the toolbar. The heading rendered on the page wins,
// since it is what the user reads: the project list heading says "Active projects"
// while the document title only says "Projects". Angular driven pages render no
// heading on load, so the document title serves as fallback and every change to it
// re-resolves the toolbar title.
export default class GpePageTitleController extends Controller {
  static values = {
    appTitle: String,
  };

  declare appTitleValue:string;

  private observer:MutationObserver|undefined;

  private listener = ():void => this.update();

  connect() {
    this.update();

    const title = document.querySelector('title');
    if (title) {
      this.observer = new MutationObserver(this.listener);
      this.observer.observe(title, { childList: true, characterData: true, subtree: true });
    }

    document.addEventListener('turbo:load', this.listener);
    document.addEventListener('turbo:frame-render', this.listener);
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = undefined;

    document.removeEventListener('turbo:load', this.listener);
    document.removeEventListener('turbo:frame-render', this.listener);
  }

  private update():void {
    const title = this.headingTitle() || this.documentTitle();

    if (!title || title === this.element.textContent) {
      return;
    }

    this.element.textContent = title;
    this.element.setAttribute('title', title);
  }

  private headingTitle():string|undefined {
    for (const selector of headingSelectors) {
      const headings = Array.from(document.querySelectorAll<HTMLElement>(selector));

      const text = headings
        .filter((heading) => !hiddenHeadingClasses.some((klass) => heading.classList.contains(klass)))
        .map((heading) => heading.textContent?.trim())
        .find((content) => !!content);

      if (text) {
        return text;
      }
    }

    return undefined;
  }

  private documentTitle():string|undefined {
    const current = document.title.split(titlePartsSeparator)[0]?.trim();

    // Without a page specific part the document title is only the application title,
    // in which case the server rendered value is the better one to keep.
    if (!current || current === this.appTitleValue) {
      return undefined;
    }

    return current;
  }
}
